import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, Vibrate, ZoomIn, Zap, Tv, Blend, Focus, Lightbulb, Film, Palette, Contrast, CircleDot, Droplet, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ProBadge } from '@/components/ProBadge';
import { DYNAMIC_EFFECT_IDS, LOOK_EFFECT_IDS, type EffectId } from '@/lib/video-effects/sections';
import { EFFECT_LIBRARY } from '@/lib/video-effects/library/registry';

const ICONS: Record<EffectId, React.ComponentType<{ className?: string }>> = {
  shake: Vibrate, 'zoom-punch': ZoomIn, flash: Zap, glitch: Tv, 'rgb-split': Blend,
  'focus-pull': Focus, strobe: Lightbulb, vhs: Film,
  'look-warm': Palette, 'look-cool': Palette, 'look-cinema': Palette, 'look-vintage': Palette,
  'look-vivid': Palette, 'look-mono': Palette, bw: Contrast, grain: Sparkles, vignette: CircleDot, blur: Droplet,
};

/** Timeline toolbar entry: pick an effect; it is dropped at the playhead. */
export function EffectsLibraryButton({ onAdd }: { onAdd: (effect: EffectId) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const card = (id: EffectId) => {
    const Icon = ICONS[id];
    return (
      <button key={id} type="button" data-testid={`effect-card-${id}`}
        className="flex flex-col items-center gap-1 rounded-md border border-border p-2 text-xs hover:border-primary hover:bg-primary/10"
        onClick={() => { onAdd(id); setOpen(false); }}>
        <Icon className="h-5 w-5" />
        <span className="truncate">{t(EFFECT_LIBRARY[id].labelKey)}</span>
      </button>
    );
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="secondary" size="sm" className="h-8 w-8 p-0" data-testid="timeline-add-effect" title={t('videoEffects.sections.add')} aria-label={t('videoEffects.sections.add')}>
          <Wand2 className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" data-layer-ui>
        {/* Like the add-layer menu: the PRO tier marker lives in the header, not on the icon trigger. */}
        <div className="mb-2 flex items-center justify-between gap-3 text-xs text-muted-foreground">
          {t('videoEffects.sections.add')}
          <ProBadge />
        </div>
        <Tabs defaultValue="dynamic">
          <TabsList className="w-full">
            <TabsTrigger value="dynamic" className="flex-1">{t('videoEffects.sections.tabDynamic')}</TabsTrigger>
            <TabsTrigger value="look" className="flex-1">{t('videoEffects.sections.tabLook')}</TabsTrigger>
          </TabsList>
          <TabsContent value="dynamic" className="grid grid-cols-4 gap-2">{DYNAMIC_EFFECT_IDS.map(card)}</TabsContent>
          <TabsContent value="look" className="grid grid-cols-4 gap-2">{LOOK_EFFECT_IDS.map(card)}</TabsContent>
        </Tabs>
      </PopoverContent>
    </Popover>
  );
}
